import React, { useCallback, useEffect, useState } from 'react';
import { Spin, Typography } from 'antd';
import {
  Archive,
  BadgeCheck,
  ChevronDown,
  ClipboardCheck,
  ClipboardPenLine,
  Copy,
  FileCheck2,
  FileClock,
  FilePlus2,
  FileWarning,
  History,
  Landmark,
  ListChecks,
  MailCheck,
  PauseCircle,
  PlayCircle,
  ReceiptText,
} from 'lucide-react';
import callApi from '../Api/config';
import { DOCUMITRA_MEMBER_STAGE_HISTORY } from '../constants/EntityName';
import { InvertFillButton } from './InvertFillButton';

const { Title, Text } = Typography;

/** One row of member-wise stage history returned by GET /application/member-stage-history/:memberId */
export interface StageHistoryEntry {
  id: string;
  action: string;
  title: string;
  description?: string | null;
  previousValue?: string | null;
  newValue?: string | null;
  actorType?: 'ADMIN' | 'USER' | 'SYSTEM' | string;
  actorName?: string | null;
  createdAt: string;
  metadata?: Record<string, any> | null;
}

interface OrderStageHistoryProps {
  memberId?: string;
  memberName?: string;
}

type EntryTone = 'latest' | 'completed';

const ACTION_ICONS: Record<string, React.ReactNode> = {
  STATUS_CHANGED: <ClipboardCheck size={20} />,
  GOVERNMENT_STATUS_CHANGED: <Landmark size={20} />,
  DOCUMENT_ACCEPTED: <FileCheck2 size={20} />,
  DOCUMENT_REJECTED: <FileWarning size={20} />,
  DOCUMENT_MARKED_DUPLICATE: <Copy size={20} />,
  DOCUMENT_DUPLICATE_CLEARED: <Copy size={20} />,
  DOCUMENT_REMARK_ADDED: <ClipboardPenLine size={20} />,
  DOCUMENT_TYPE_ASSIGNED: <ListChecks size={20} />,
  MISSING_DOCUMENTS_EMAIL_SENT: <MailCheck size={20} />,
  FORM_UPLOADED: <FilePlus2 size={20} />,
  FORM_UPDATED: <FilePlus2 size={20} />,
  FORM_DELETED: <Archive size={20} />,
  FORM_AUTO_DELETED: <Archive size={20} />,
  USER_FORM_ACCEPTED: <BadgeCheck size={20} />,
  USER_FORM_REJECTED: <FileWarning size={20} />,
  USER_FORM_REMARK_ADDED: <ClipboardPenLine size={20} />,
  TRACKING_DETAILS_UPDATED: <FileClock size={20} />,
  NOTE_ADDED: <ReceiptText size={20} />,
  MEMBER_PUT_ON_HOLD: <PauseCircle size={20} />,
  MEMBER_HOLD_RELEASED: <PlayCircle size={20} />,
};

const ACTOR_TYPE_LABELS: Record<string, string> = {
  ADMIN: 'Documitra team',
  USER: 'Applicant',
  SYSTEM: 'System',
};

const getToneStyles = (tone: EntryTone) => {
  if (tone === 'latest') {
    return {
      iconBg: '#fff7e6',
      iconColor: '#fa8c16',
      dotBg: '#fa8c16',
      lineBg: '#d9d9d9',
      cardBg: '#fffbf0',
      cardBorder: '#e6edf5',
      badgeBg: '#fff3e0',
      badgeColor: '#e65100',
      badgeLabel: 'Latest',
    };
  }
  return {
    iconBg: '#e6f7ff',
    iconColor: '#075dc5',
    dotBg: '#075dc5',
    lineBg: '#075dc5',
    cardBg: '#fafcff',
    cardBorder: '#e6edf5',
    badgeBg: '#e6f4ea',
    badgeColor: '#2e7d32',
    badgeLabel: 'Completed',
  };
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatTime = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const getTimestamp = (value?: string) => {
  const timestamp = value ? new Date(value).getTime() : Number.NaN;
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getActorLabel = (entry: StageHistoryEntry) => {
  const typeLabel = ACTOR_TYPE_LABELS[String(entry.actorType || '').toUpperCase()] || '';
  const name = String(entry.actorName || '').trim();
  if (name && typeLabel && name.toLowerCase() !== typeLabel.toLowerCase()) {
    return `${name} · ${typeLabel}`;
  }
  return name || typeLabel || 'System';
};

interface HistoryTimelineItem {
  entry: StageHistoryEntry;
  children: StageHistoryEntry[];
  latestActivityAt: number;
}

const buildHistoryTimeline = (entries: StageHistoryEntry[]): HistoryTimelineItem[] => {
  const chronologicalEntries = [...entries].sort(
    (a, b) => getTimestamp(a.createdAt) - getTimestamp(b.createdAt)
  );
  const timeline: HistoryTimelineItem[] = [];
  let currentStatusItem: HistoryTimelineItem | null = null;

  // Associate later actions with the active status change; prior actions stay top-level.
  chronologicalEntries.forEach(entry => {
    if (entry.action === 'STATUS_CHANGED') {
      currentStatusItem = {
        entry,
        children: [],
        latestActivityAt: getTimestamp(entry.createdAt),
      };
      timeline.push(currentStatusItem);
      return;
    }

    if (currentStatusItem) {
      currentStatusItem.children.push(entry);
      currentStatusItem.latestActivityAt = Math.max(
        currentStatusItem.latestActivityAt,
        getTimestamp(entry.createdAt)
      );
      return;
    }

    timeline.push({
      entry,
      children: [],
      latestActivityAt: getTimestamp(entry.createdAt),
    });
  });

  timeline.forEach(item => {
    item.children.sort((a, b) => getTimestamp(b.createdAt) - getTimestamp(a.createdAt));
  });

  return timeline.sort((a, b) => b.latestActivityAt - a.latestActivityAt);
};

const EntryDetails: React.FC<{
  entry: StageHistoryEntry;
  tone: EntryTone;
  showTitle?: boolean;
  showTime?: boolean;
}> = ({ entry, tone, showTitle = true, showTime = true }) => {
  const actorLabel = getActorLabel(entry);
  const hasValueChange = !!entry.newValue && (entry.previousValue || '') !== (entry.newValue || '');
  const styles = getToneStyles(tone);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: showTitle || showTime ? '6px' : 0,
        }}
      >
        {showTitle && (
          <Title
            level={5}
            style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: 700,
              color: '#1a1a1a',
            }}
          >
            {entry.title}
            <span style={{ color: '#bfbfbf' }}> · </span>
            <Text style={{ color: '#60758d', fontWeight: 500, fontSize: '13px' }}>
              {actorLabel}
            </Text>
          </Title>
        )}
        {showTime && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text style={{ color: '#60758d', fontSize: '12px' }}>
              {formatTime(entry.createdAt)}
            </Text>
            {showTitle && (
              <span
                style={{
                  padding: '2px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: styles.badgeBg,
                  color: styles.badgeColor,
                }}
              >
                {styles.badgeLabel}
              </span>
            )}
          </div>
        )}
      </div>

      {hasValueChange && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '6px',
            marginBottom: entry.description ? '8px' : 0,
            fontSize: '13px',
          }}
        >
          {entry.previousValue && (
            <>
              <span
                style={{
                  padding: '1px 8px',
                  borderRadius: '6px',
                  background: '#f1f5f9',
                  color: '#64748b',
                  textDecoration: 'line-through',
                }}
              >
                {entry.previousValue}
              </span>
              <span style={{ color: '#94a3b8' }}>→</span>
            </>
          )}
          <span
            style={{
              padding: '1px 8px',
              borderRadius: '6px',
              background: '#e6eefb',
              color: '#004195',
              fontWeight: 600,
            }}
          >
            {entry.newValue}
          </span>
        </div>
      )}

      {entry.description && (
        <Text
          style={{
            fontSize: '14px',
            color: '#595959',
            lineHeight: 1.55,
            display: 'block',
            whiteSpace: 'pre-wrap',
          }}
        >
          {entry.description}
        </Text>
      )}
    </>
  );
};

/**
 * Member-wise Order Stage History: every action / status change performed for this member,
 * newest first. Data comes from the backend member_stage_history table.
 */
const OrderStageHistory: React.FC<OrderStageHistoryProps> = ({ memberId, memberName }) => {
  const [entries, setEntries] = useState<StageHistoryEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [expandedSubActivities, setExpandedSubActivities] = useState<Set<string>>(() => new Set());

  const fetchHistory = useCallback(async () => {
    if (!memberId) {
      setEntries([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await callApi(`${DOCUMITRA_MEMBER_STAGE_HISTORY}/${memberId}`, 'GET');
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      if (res?.status || res?.data?.statusCode === 200) {
        setEntries(list);
      } else {
        setError(res?.data?.message || 'Unable to load stage history');
      }
    } catch (err) {
      console.error('Error loading stage history:', err);
      setError('Unable to load stage history');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const sortedEntries = [...entries].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  const timelineItems = buildHistoryTimeline(sortedEntries);
  const latestEntryId = sortedEntries[0]?.id;

  return (
    <div
      style={{
        width: '100%',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        background: '#fff',
        padding: '24px 28px',
        marginBottom: '18px',
      }}
    >
      <div
        style={{
          fontSize: '16px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'black',
          marginBottom: '18px',
          paddingBottom: '12px',
          borderBottom: '1px solid #e2e8f0',
          lineHeight: 1.2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <History size={18} color="#075dc5" />
          Order Stage History
          {/* {memberName && (
            <span
              style={{
                padding: '1px 9px',
                borderRadius: '999px',
                border: '1px solid #bfdbfe',
                background: '#eff6ff',
                color: '#1d4ed8',
                fontSize: '12px',
                fontWeight: 700,
                textTransform: 'none',
                letterSpacing: 'normal',
                whiteSpace: 'nowrap',
              }}
            >
              {memberName}
            </span>
          )} */}
        </span>
        <InvertFillButton
          variant="white"
          size="sm"
          fontSize="sm"
          fontWeight="semibold"
          onClick={fetchHistory}
          disabled={loading || !memberId}
        >
          Refresh
        </InvertFillButton>
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <Spin />
        </div>
      )}

      {!loading && error && (
        <Text type="danger" style={{ display: 'block' }}>
          {error}
        </Text>
      )}

      {!loading && !error && sortedEntries.length === 0 && (
        <Text type="secondary" style={{ display: 'block' }}>
          No actions have been recorded for this member yet. Status changes, document reviews,
          uploads, notes and emails will appear here.
        </Text>
      )}

      {!loading && !error && sortedEntries.length > 0 && (
        <div style={{ position: 'relative', paddingLeft: '175px' }}>
          {timelineItems.map((item, index) => {
            const { entry } = item;
            const tone: EntryTone = entry.id === latestEntryId ? 'latest' : 'completed';
            const styles = getToneStyles(tone);
            const isLast = index === timelineItems.length - 1;
            const entryDate = formatDate(new Date(item.latestActivityAt).toISOString());
            const previousDate =
              index > 0
                ? formatDate(new Date(timelineItems[index - 1].latestActivityAt).toISOString())
                : null;
            const showDate = entryDate !== previousDate;

            return (
              <div
                key={`${entry.id}-${index}`}
                style={{
                  position: 'relative',
                  paddingBottom: isLast ? '0px' : '28px',
                }}
              >
                {showDate && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '-210px',
                      top: '1px',
                      width: '124px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-end',
                      zIndex: 2,
                    }}
                  >
                    <div
                      style={{
                        padding: '0px 12px',
                        background: '#e6f4ff',
                        border: '1px solid #91caff',
                        borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(7, 93, 197, 0.1)',
                      }}
                    >
                      <Text
                        style={{
                          fontSize: '12px',
                          fontWeight: 700,
                          color: '#075dc5',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {entryDate}
                      </Text>
                    </div>
                  </div>
                )}

                {!isLast && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '-66px',
                      top: '32px',
                      bottom: '0px',
                      width: '2px',
                      background: styles.lineBg,
                    }}
                  />
                )}

                <div
                  style={{
                    position: 'absolute',
                    left: '-80px',
                    top: '0px',
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    background: styles.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: styles.iconColor,
                    border: `2px solid ${styles.dotBg}`,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                    zIndex: 1,
                  }}
                >
                  {ACTION_ICONS[entry.action] || <ClipboardCheck size={20} />}
                </div>

                <div
                  style={{
                    background: styles.cardBg,
                    borderRadius: '8px',
                    border: `1px solid ${styles.cardBorder}`,
                    padding: '14px 18px',
                    transition: 'box-shadow 0.2s ease',
                  }}
                >
                  <EntryDetails entry={entry} tone={tone} />

                  {item.children.length > 0 && (
                    <div
                      style={{
                        marginTop: '14px',
                        marginLeft: '8px',
                        paddingLeft: '18px',
                      }}
                    >
                      <Text
                        style={{
                          display: 'block',
                          marginBottom: '9px',
                          color: '#075dc5',
                          fontSize: '12px',
                          fontWeight: 700,
                        }}
                      >
                        Sub-activity during this status · {item.children.length}
                      </Text>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {item.children.map((child, childIndex) => {
                          const childTone: EntryTone =
                            child.id === latestEntryId ? 'latest' : 'completed';
                          const childStyles = getToneStyles(childTone);
                          const isExpanded = expandedSubActivities.has(child.id);
                          const detailsId = `sub-activity-details-${child.id}`;

                          return (
                            <div
                              key={child.id}
                              style={{
                                position: 'relative',
                              }}
                            >
                              {childIndex < item.children.length - 1 && (
                                <span
                                  aria-hidden="true"
                                  style={{
                                    position: 'absolute',
                                    left: '-20px',
                                    top: '22px',
                                    bottom: '-30px',
                                    borderLeft: `2px dotted ${styles.lineBg}`,
                                    zIndex: 0,
                                  }}
                                />
                              )}
                              <span
                                style={{
                                  position: 'absolute',
                                  left: '-24px',
                                  top: '17px',
                                  width: '10px',
                                  height: '10px',
                                  boxSizing: 'border-box',
                                  borderRadius: '50%',
                                  background: styles.dotBg,
                                  border: '2px solid #fff',
                                  boxShadow: `0 0 0 1px ${styles.lineBg}`,
                                  zIndex: 1,
                                }}
                              />
                              <div
                                style={{
                                  border: `1px solid ${styles.cardBorder}`,
                                  borderRadius: '7px',
                                  background: '#fff',
                                  overflow: 'hidden',
                                }}
                              >
                                <button
                                  type="button"
                                  aria-expanded={isExpanded}
                                  aria-controls={detailsId}
                                  onClick={() => {
                                    setExpandedSubActivities(previous => {
                                      const next = new Set(previous);
                                      if (next.has(child.id)) {
                                        next.delete(child.id);
                                      } else {
                                        next.add(child.id);
                                      }
                                      return next;
                                    });
                                  }}
                                  style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '12px',
                                    padding: '11px 14px',
                                    border: 0,
                                    background: '#fff',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <Text
                                    strong
                                    style={{
                                      color: '#1a1a1a',
                                      fontSize: '14px',
                                    }}
                                  >
                                    {child.title}
                                  </Text>
                                  <span
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Text style={{ color: '#60758d', fontSize: '12px' }}>
                                      {formatTime(child.createdAt)}
                                    </Text>
                                    <span
                                      style={{
                                        padding: '2px 9px',
                                        borderRadius: '12px',
                                        background: childStyles.badgeBg,
                                        color: child.id === latestEntryId ? '#e65100' : '#2e7d32',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {child.id === latestEntryId ? 'Latest' : 'Completed'}
                                    </span>
                                    <ChevronDown
                                      size={16}
                                      color="#60758d"
                                      style={{
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease',
                                      }}
                                    />
                                  </span>
                                </button>
                                <div
                                  id={detailsId}
                                  hidden={!isExpanded}
                                  style={{
                                    padding: '12px 14px',
                                    borderTop: `1px solid ${styles.cardBorder}`,
                                    background: '#fff',
                                  }}
                                >
                                  {isExpanded && (
                                    <>
                                      <Text
                                        style={{
                                          display: 'block',
                                          marginBottom: '10px',
                                          color: '#60758d',
                                          fontSize: '12px',
                                        }}
                                      >
                                        Action by {getActorLabel(child)}
                                      </Text>
                                      <EntryDetails
                                        entry={child}
                                        tone={childTone}
                                        showTitle={false}
                                        showTime={false}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderStageHistory;
